import {
  Button,
  EmptyState,
  FilterSegments,
  ListRow,
  ListRows,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  formatCurrency,
  formatDate,
  titleCase,
  useBreakpoint,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import type { Patient } from '@/features/patients/api'
import { routes } from '@/routes'
import { useTreatmentPlans, type TreatmentPlanRow } from './api'
import { EMPTY_PATIENT, stagesCell } from './labels'

/**
 * A aba Planos de tratamento da ficha da paciente (docs/areas/pacientes.md, "Abas";
 * `GET /treatment-plans?patientId=`): Em aberto · Encerrados, "+ Novo plano", colunas CRIADO ·
 * PROCEDIMENTOS · PROFISSIONAL · TOTAL · ETAPAS ("1 de 4" em plum quando incompleto). No celular,
 * cartões "… · Fotos 1 de 4 · Abrir". A ficha (`features/patients`) só a encaixa.
 */

type PatientPlanFilter = 'open' | 'closed'

export function PatientTreatmentPlansTab({ patient }: { patient: Patient }) {
  const navigate = useNavigate()
  const isDesk = useBreakpoint('desk')
  const [filter, setFilter] = useState<PatientPlanFilter>('open')
  const [page, setPage] = useState(1)
  const query = useTreatmentPlans({ filter, patientId: patient.id, page, perPage: 10 })
  const rows = query.data?.data ?? []
  const counts = query.data?.meta.counts
  const canCreate = patient.status === 'active' && !patient.isReadOnly
  const options: FilterSegmentOption<PatientPlanFilter>[] = [
    { value: 'open', label: 'Em aberto', count: counts?.open },
    { value: 'closed', label: 'Encerrados', count: counts?.closed },
  ]
  const hasAny = (counts?.open ?? 0) + (counts?.closed ?? 0) > 0

  function open(row: TreatmentPlanRow) {
    navigate(routes.treatmentPlan(row.id))
  }

  const newPlan = canCreate ? (
    <Button variant="outline" size="sm" asChild>
      <Link to={routes.newTreatmentPlan(patient.id)}>+ Novo plano</Link>
    </Button>
  ) : null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="eyebrow">
          Planos de tratamento{counts ? ` · ${counts.open + counts.closed}` : ''}
        </h2>
        {hasAny ? newPlan : null}
      </div>

      {query.isError ? (
        <LoadError
          title="Não conseguimos carregar os planos desta paciente"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : query.isPending ? (
        <TableSkeleton rows={3} columns={5} />
      ) : !hasAny ? (
        <EmptyState
          title={EMPTY_PATIENT.title}
          description={`${EMPTY_PATIENT.description} Ele nasce quando o contrato de prestação é gerado, ou à mão.`}
          action={
            canCreate ? (
              <Button variant="outline" asChild>
                <Link to={routes.newTreatmentPlan(patient.id)}>Criar primeiro plano</Link>
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <FilterSegments
            label="Filtrar planos da paciente"
            options={options}
            value={filter}
            onChange={(next) => {
              setFilter(next)
              setPage(1)
            }}
          />
          {rows.length === 0 ? (
            <EmptyState
              title={filter === 'open' ? 'Nenhum plano em aberto' : 'Nenhum plano encerrado'}
              description={
                filter === 'open'
                  ? 'Os planos desta paciente já foram encerrados.'
                  : 'Os planos desta paciente ainda estão em aberto.'
              }
              action={
                <Button
                  variant="outline"
                  onClick={() => setFilter(filter === 'open' ? 'closed' : 'open')}
                >
                  {filter === 'open' ? 'Ver encerrados' : 'Ver em aberto'}
                </Button>
              }
            />
          ) : isDesk ? (
            <Table>
              <TableHeader>
                <tr>
                  <TableHead>Criado</TableHead>
                  <TableHead>Procedimentos</TableHead>
                  <TableHead>Profissional</TableHead>
                  <TableHead numeric>Total</TableHead>
                  <TableHead>Etapas</TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const stages = stagesCell(row.recordedStageCount, row.status)
                  return (
                    <TableRow key={row.id} onClick={() => open(row)}>
                      <TableCell label="Criado" muted>
                        {formatDate(new Date(row.createdAt), { year: false })}
                      </TableCell>
                      <TableCell label="Procedimentos" primary>
                        {row.procedures.join(', ')}
                      </TableCell>
                      <TableCell label="Profissional" muted>
                        {titleCase(row.practitioner.name)}
                      </TableCell>
                      <TableCell label="Total" numeric>
                        {formatCurrency(row.totalAmountCents / 100, { compact: true })}
                      </TableCell>
                      <TableCell
                        label="Etapas"
                        className={stages.pending ? 'text-plum' : undefined}
                        muted={!stages.pending}
                      >
                        {stages.text}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          ) : (
            <ListRows>
              {rows.map((row) => {
                const stages = stagesCell(row.recordedStageCount, row.status)
                return (
                  <ListRow
                    key={row.id}
                    size="name"
                    title={row.procedures.join(', ')}
                    meta={`${formatDate(new Date(row.createdAt), { year: false })} · ${titleCase(row.practitioner.name)} · ${formatCurrency(row.totalAmountCents / 100, { compact: true })}`}
                    trailing={
                      <div className="flex flex-col items-end gap-2">
                        {stages.pending ? (
                          <StatusTag level="attention">Fotos {stages.text}</StatusTag>
                        ) : (
                          <StatusTag level="done">
                            {row.status === 'closed' ? 'Encerrado' : `Fotos ${stages.text}`}
                          </StatusTag>
                        )}
                        <Button variant="text" size="sm" asChild>
                          <Link to={routes.treatmentPlan(row.id)}>Abrir</Link>
                        </Button>
                      </div>
                    }
                    onClick={() => open(row)}
                  />
                )
              })}
            </ListRows>
          )}
          {query.data && rows.length > 0 ? (
            <ListFooter
              meta={query.data.meta}
              noun={['plano', 'planos']}
              note="a linha abre o plano"
              onPage={setPage}
            />
          ) : null}
        </>
      )}
    </div>
  )
}
